import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useAPI from "../../../../hooks/useApi";
import useAccount from "../../../../hooks/useAccount";
import { useHubSpotTracking } from "../../../../hooks/useHubSpotTracking";
import Cookies from "universal-cookie";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  // eslint-disable-next-line
  const { post, get, error } = useAPI();
  const queryClient = useQueryClient();
  const { identifyUser, trackEvent } = useHubSpotTracking();

  // Use shared React Query hook for account data
  const { data: accountData, isLoading: isAuthLoading } = useAccount();

  // Derive auth state from account data
  const user = accountData?.email ? accountData : null;
  const isAuth = !!user;

  // Identify user to HubSpot for event tracking
  useEffect(() => {
    if (user?.email) {
      identifyUser(user.email);
    }
  }, [user?.email, identifyUser]);

  // Handle redirect from signin page when authenticated
  useEffect(() => {
    if (isAuth && window.location.pathname === "/signin") {
      const redirectLink = document.createElement("a");
      redirectLink.href = "/";
      document.body.appendChild(redirectLink);
      redirectLink.click();
    }
  }, [isAuth]);

  const checkIfAcceptedTOS = useCallback(async () => {
    try {
      if (isAuth) {
        const results = await get(`/api/core/terms`, () => {});
        setHasAcceptedTerms(results?.tos_accepted);
      }
    } catch (e) {
      console.error("checkIfAcceptedTOS error", e);
      throw e;
    }
  }, [isAuth, get])

  useEffect(() => {
    checkIfAcceptedTOS();
  }, [checkIfAcceptedTOS]);

  const acceptedTOS = useCallback(async () => {
    try {
      if (isAuth) {
        const resp = await post(`/api/core/terms`, { accept_tos: true })
        setHasAcceptedTerms(resp?.result === "ok");
      }
    } catch (e) {
      console.error("acceptedTOS error", e);
      throw e;
    }
  }, [isAuth, post])

  const login = () => {
    // Track login event
    trackEvent("user_login");
    // Invalidate account query to refetch user data
    queryClient.invalidateQueries({ queryKey: ["getUserAccount"] });
  };

  const logout = async () => {
    await post(`/api/auth/logout`, () => {});
    const cookies = new Cookies();

    //Clear all cookies
    cookies.remove("csrftoken");
    cookies.remove("sessionid");

    // Clear account data from cache and redirect before refetch can occur
    queryClient.setQueryData(["getUserAccount"], null);
    queryClient.cancelQueries({ queryKey: ["getUserAccount"] });
    window.location.href = "/signin";
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuth,
        isAuthLoading,
        login,
        logout,
        hasAcceptedTerms,
        acceptedTOS,
        isImpersonating,
        setIsImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
